import React from "react";
import { FileText, ShieldCheck, Zap, ClipboardCheck } from "lucide-react";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { PageHero, SectionHeading, FeatureCard, CtaBanner } from "@/components/marketing/blocks";
import { useSeo } from "@/hooks/useSeo";

const PILLARS = [
  { icon: FileText, title: "Documentation", desc: "Every attempt and detail is recorded, so your proof of service is thorough and clear." },
  { icon: ShieldCheck, title: "Integrity", desc: "We handle sensitive legal matters with honesty, discretion, and professionalism." },
  { icon: Zap, title: "Speed", desc: "We move quickly on time-sensitive filings, with rush and 24-Hour Priority options available." },
  { icon: ClipboardCheck, title: "Accountability", desc: "GPS, timestamps, and photos create an audit-ready trail you can rely on." },
];

export default function About() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useSeo({
    title: "About AR Process Service | Memphis, TN",
    description:
      "AR Process Service is a Memphis-based process serving and notary provider committed to integrity, precision, and complete document traceability across the Memphis metro and Shelby County.",
    canonicalPath: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About AR Process Service",
      url: `${origin}/about`,
    },
  });

  return (
    <MarketingLayout>
      <PageHero
        overline="Integrity First"
        title="About AR Process Service and Notary"
        subtitle="We are a Memphis-based process serving and notary company built on a simple idea: legal service should be precise, professional, and fully documented from start to finish."
      />

      <section className="px-5 py-14 sm:px-8 lg:py-20">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Our mission</h2>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            Integrity first: law firms, businesses, and individuals in the Memphis area need to know their documents were
            handled correctly and can be proven so. AR Process Service brings a disciplined, evidence-first approach to
            every job — capturing GPS locations, timestamps, and photos, then delivering court-ready affidavits that
            stand up to scrutiny.
          </p>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            Whether you need documents served across Shelby County or a mobile notary for a signing in Germantown, our
            focus never changes: every service, every attempt, and every detail — documented.
          </p>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white px-5 py-14 sm:px-8 lg:py-20">
        <SectionHeading overline="What we stand for" title="Our core pillars" />
        <div className="mx-auto mt-10 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p) => (
            <FeatureCard key={p.title} icon={p.icon} title={p.title}>
              {p.desc}
            </FeatureCard>
          ))}
        </div>
      </section>

      <CtaBanner title="Work with a partner who documents everything" />
    </MarketingLayout>
  );
}
